import { useMe } from '@/data/hooks';
import { useAuth } from './AuthContext';
import type { SessionUser } from './claims';

/**
 * The display profile for the signed-in user. Precedence:
 *   1. `GET /me` (server-verified — the source of truth once loaded)
 *   2. JWT claims captured at sign-in (the immediate bootstrap)
 *
 * Guards may rely on the bootstrap session for the role (available instantly),
 * but anything user-facing (name, links) should prefer `/me` so an admin who
 * edits a profile sees the change without re-login. `/me` is only fetched while
 * authenticated. Either way the backend remains the authority on access.
 */
export function useSessionProfile(): {
  profile: SessionUser | null;
  /** True while `/me` is still loading and only the claims bootstrap is available. */
  isResolving: boolean;
} {
  const { session, isAuthenticated } = useAuth();
  const me = useMe(isAuthenticated);

  if (!session) return { profile: null, isResolving: false };

  const bootstrap = session.user;
  const server = me.data;

  const profile: SessionUser = server
    ? {
        userId: server.userId,
        email: server.email,
        fullName: server.fullName,
        role: server.role,
        locale: server.locale,
        customerId: server.customerId ?? bootstrap.customerId,
        distributorId: server.distributorId ?? bootstrap.distributorId,
        salesRepId: server.salesRepId ?? bootstrap.salesRepId,
      }
    : bootstrap;

  return { profile, isResolving: me.isLoading };
}
